import { appConfig } from "@/config/app";
import { apiFetch } from "@/services/api";

export interface MeResponse {
  user_id: string;
  email: string | null;
  roles: string[];
  provider_ids: string[];
}

/** Initial attempt + retries. Floor at 6 so a slow local uvicorn --reload race can recover. */
const MAX_ATTEMPTS = Math.max(appConfig.api.retries + 1, 6);
const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 2_000;

export type FetchMeFn = () => Promise<MeResponse>;

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });
}

/**
 * Retries GET /me with short exponential backoff so a backend-not-ready race
 * (common on local FE+BE start) does not look like “user has no providers.”
 * Returns null only after all attempts fail or the signal aborts.
 */
export async function fetchMeWithRetry(
  options: { signal?: AbortSignal; fetchMe?: FetchMeFn } = {}
): Promise<MeResponse | null> {
  const fetchMe = options.fetchMe ?? (() => apiFetch<MeResponse>("/me"));
  const { signal } = options;

  let lastError: unknown;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (signal?.aborted) return null;
    try {
      return await fetchMe();
    } catch (err) {
      lastError = err;
      if (signal?.aborted) return null;
      const isLast = attempt >= MAX_ATTEMPTS - 1;
      if (isLast) break;
      const waitMs = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
      try {
        await delay(waitMs, signal);
      } catch {
        return null;
      }
    }
  }

  if (lastError && import.meta.env.DEV) {
    console.warn("[fetchMeWithRetry] GET /me failed after retries", lastError);
  }
  return null;
}

/** Exported for unit tests so attempt/delay math stays documented in one place. */
export const fetchMeWithRetryTestHooks = {
  MAX_ATTEMPTS,
  BASE_DELAY_MS,
  MAX_DELAY_MS,
};
